/**
 * Task 9.6 — a minimal toast layer replacing the 30+ bare window-alert
 * calls scattered across the views. `useToast()` returns `{success, error, info}`
 * helpers; `<ToastHost/>` (mounted once in App.tsx) renders the queue and
 * auto-dismisses each toast after a few seconds.
 */
import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";
import "./toast.css";

export type ToastKind = "success" | "error" | "info";

export interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastContextValue {
  toasts: ToastItem[];
  push: (kind: ToastKind, message: string) => void;
  dismiss: (id: number) => void;
}

/** Default (provider-less) context: swallows toasts instead of throwing, so
 * existing view tests that render a component in isolation (no
 * `<ToastProvider>` wrapper) keep working. `ToastHost` always runs inside
 * the real provider in App.tsx, so this default's empty `toasts` array
 * never actually renders anything in production. */
const noopContext: ToastContextValue = {
  toasts: [],
  push: () => {},
  dismiss: () => {},
};

const ToastContext = createContext<ToastContextValue>(noopContext);

const AUTO_DISMISS_MS = 5000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, message: string) => {
      const id = nextId.current++;
      setToasts((t) => [...t, { id, kind, message }]);
      setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
    },
    [dismiss]
  );

  return (
    <ToastContext.Provider value={{ toasts, push, dismiss }}>
      {children}
    </ToastContext.Provider>
  );
}

/** Read-only queue + dismiss, for the host component. */
export function useToastQueue() {
  return useContext(ToastContext);
}

/** The hook views actually call: `toast.error("failed: " + e.message)`. */
export function useToast() {
  const { push } = useContext(ToastContext);
  return {
    success: (message: string) => push("success", message),
    error: (message: string) => push("error", message),
    info: (message: string) => push("info", message),
  };
}

export function ToastHost() {
  const { toasts, dismiss } = useToastQueue();
  if (toasts.length === 0) return null;
  return (
    <div className="toast-host" role="status" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={`toast toast-${t.kind}`}>
          <span className="toast-message">{t.message}</span>
          <button
            className="toast-dismiss"
            aria-label="Dismiss"
            onClick={() => dismiss(t.id)}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
